import { createSelector } from 'reselect'
import type { MachineContext } from 'typings/app/machineContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'
import { isLEHotel } from 'lib/offer/offerTypes'
import { getEffectiveBundledWithFlightsOnly, getIsFlightsSelected } from './offerPage/offerPageSelectors'

/**
 * These products have their own cancellation terms, so a hotel policy must clarify its
 * coverage when one accompanies the accommodation. Extra rooms, insurance/protection,
 * membership and fees do not trigger that wording: they are not another travel product.
 */
const otherTravelProductTypes = new Set<App.Checkout.AnyItemType>([
  'flight',
  'experience',
  'transfer',
  'car-hire',
  'tourV1',
  'tourV2',
  'tourV2Experience',
  'cruise',
  'loungePassItem',
  'loyaltyTransferItem',
])

/**
 * Cart entries represent products actually selected by the customer. Checking them avoids
 * qualifying a hotel policy just because its offer advertises optional flights or extras.
 * Package-contained transfers also count, even though they have no separate cart entry.
 */
export function hasOtherSelectedTravelProducts(
  items: ReadonlyArray<Pick<App.Checkout.AnyItem, 'itemType'>>,
  offers?: App.State['offer']['offers'],
): boolean {
  return items.some(item => {
    if (otherTravelProductTypes.has(item.itemType)) {
      return true
    }

    if (
      item.itemType !== 'hotel' || !('offerId' in item) || !('packageId' in item) ||
      typeof item.offerId !== 'string' || typeof item.packageId !== 'string'
    ) {
      return false
    }

    const offer = offers?.[item.offerId]
    if (!isLEHotel(offer)) {
      return false
    }

    // Inspect the selected package/rate only; another available rate may include a transfer.
    const pkg = offer.packages.find(pkg =>
      pkg.id === item.packageId &&
      (!('roomRateId' in item) || pkg.roomRate?.id === item.roomRateId),
    )

    return !!pkg && hasAirportTransferPackageInclusions(pkg)
  })
}

/**
 * Shared carts retain deleted and saved-for-later entries alongside the current selection.
 * Resolve only active entries before applying the same coverage rule used by checkout,
 * whose item list already contains the selected products and has no lifecycle field.
 */
export function getShouldQualifyMultiCartHotelCancellation(
  cartItems: MachineContext.MultiCartMachineContext['cartItems'],
  activeCartId: string | undefined,
  offers: App.State['offer']['offers'],
): boolean {
  if (cartItems.status !== 'loaded' || !activeCartId) {
    return false
  }

  const selectedItems = Object.values(cartItems.data[activeCartId] ?? {})
    .filter(cartItem => cartItem.state === 'active')
    .map(cartItem => cartItem.item)

  return hasOtherSelectedTravelProducts(selectedItems, offers)
}

/**
 * After booking, the order is the source of truth for purchased travel products. Check its
 * product flags and item collections, plus the saved hotel inclusions for bundled transfers,
 * so the wording does not depend on what the live offer happens to advertise today.
 */
export function hasOtherOrderTravelProducts(order: App.Order): boolean {
  return (
    order.hasFlight || order.hasExperience ||
    order.flightItems.length > 0 || order.experienceItems.length > 0 ||
    order.transferItems.length > 0 || order.carHireItems.length > 0 ||
    order.addonItems.length > 0 || order.tourItems.length > 0 ||
    order.tourExperienceItems.length > 0 || order.cruiseItems.length > 0 ||
    order.items.some(item => hasAirportTransferPackageInclusions(item.inclusions))
  )
}

/**
 * Checkout can contain a new booking or additions to an existing order. Either the current
 * selection or a previously purchased travel product makes the hotel policy accommodation-only.
 * Including the offer lookup lets package-contained transfers participate in the same rule.
 */
export const getShouldQualifyHotelCancellation = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.offer.offers,
  (items, existingOrder, offers) => (
    hasOtherSelectedTravelProducts(items, offers) ||
    (!!existingOrder && hasOtherOrderTravelProducts(existingOrder))
  ),
)

/**
 * Before cart creation, use the active offer-page selection. Selected flights and mandatory
 * flight bundles always qualify; otherwise, only transfers in the selected hotel packages count.
 * This is booking-wide, so a transfer selected for one room also qualifies the other rooms.
 */
export function getShouldQualifyOfferHotelCancellation(state: OfferPageState): boolean {
  // A configured charter route survives switching to hotel-only mode. The effective selectors
  // respect that switch; the raw force-bundle flag is only a fallback outside the charter flow.
  if (
    getIsFlightsSelected(state) || getEffectiveBundledWithFlightsOnly(state) ||
    (!state.charterBundleRoute && !!state.offer?.forceBundleId)
  ) {
    return true
  }

  if (!isLEHotel(state.offer)) {
    return false
  }

  // Match the single-package selection or each room's current rate, not every available package.
  return state.offer.packages.some(pkg => {
    const isSelectedPackage = pkg.uniqueKey === state.selectedPackageKey
    const isSelectedRoomRate = state.rooms.some(room =>
      room.roomId === pkg.id && room.rateId === pkg.roomRate?.id,
    )

    return (isSelectedPackage || isSelectedRoomRate) && hasAirportTransferPackageInclusions(pkg)
  })
}
